import React from 'react';
import { observer } from 'mobx-react';
import { Button } from 'primereact/button';
import { ArrowLeft, CheckCircle2, Download, Mail, Paperclip } from 'lucide-react';
import { OOCForecastAdjustment, OOCProvFacilityRow, formatOOCDate } from '../oocForecast.type';
import { useOOCCommunicateLogic, getCellClass, formatCellValue, periodToLabel } from './oocCommunicate.logic';
import { generate15MonthPeriods } from 'data/fdp/oocMockData';
import styles from './oocCommunicate.module.scss';

// Row grouping mirrors the canonical Provisional Forecast MAS grid
// (ProvisionalForecastV2/Edit/MasForecastGrid.tsx) and OOCCreateEdit's
// renderProvMasGrid: everything except MTF/TMOP is grouped under "CMOP"
// for subtotal purposes (this includes "VA Facility"/VISN rows).
const NON_CMOP_FACILITY_COTS = new Set(['MTF', 'TMOP']);

const ProvNdcHeader: React.FC<{ adj: OOCForecastAdjustment }> = ({ adj }) => (
  <div className={styles.provNdcHeader}>
    <span className={styles.provNdcBadge}>{adj.ndc11}</span>
    <span className={styles.provNdcMeta}>{adj.concept || '-'}</span>
    <span className={styles.provNdcMeta}>Reason: {adj.reason || '-'}</span>
  </div>
);

const ProvMasEmailTable: React.FC<{ adjustments: OOCForecastAdjustment[] }> = ({ adjustments }) => {
  if (adjustments.length === 0) return null;
  const periods = generate15MonthPeriods();

  return (
    <div>
      <div className={styles.sectionTitleRow}>
        <div className={styles.emailSectionTitle}>Provisional MAS</div>
      </div>
      {adjustments.map((adj) => {
        const rows = adj.facilityRows ?? [];

        if (rows.length === 0) {
          return (
            <div key={adj.id} className={styles.provNdcBlock}>
              <ProvNdcHeader adj={adj} />
              <div className={styles.provEmptyNote}>No facility data available for this NDC.</div>
            </div>
          );
        }

        // Matches ProvisionalForecastV2/Edit/MasForecastGrid.tsx exactly: EU
        // is annualVolSU (already in EU terms) times the month's percentage —
        // no /12, no pack-size division. Subtotals/totals sum the raw
        // (unrounded) values and round once at display, same as canonical.
        const packNum = parseInt(adj.packOut, 10) || 1;
        const monthlyEU = (row: OOCProvFacilityRow, mi: number): number => {
          if (!row.isSelected) return 0;
          const pct = row.monthlyPercentages[mi] ?? 0;
          return row.annualVolSU * (pct / 100);
        };

        // SU (Selling Units) — matches ProvisionalForecastV2/Edit/SUPricesTable.tsx
        // and OOCCreateEdit's renderProvMasGrid: Σ(annual_market / packsize)
        // across CHECKED packs for the facility, summed once, then scaled by
        // the month's %. Falls back to EU / this NDC's own pack size when the
        // facility has no AnnualMarketSales pack-size data. Raw (unrounded)
        // so subtotal/total can sum-then-round-once, same as EU.
        const calcPacks = new Set(
          (adj.selectedPackSizes ?? [])
            .filter((p) => p.selected && p.used_in_calc)
            .map((p) => p.packsize)
        );
        const annualSUByFacility = new Map<string, number>();
        for (const mktRow of adj.marketData ?? []) {
          if (!calcPacks.has(mktRow.packsize)) continue;
          const packSize = parseFloat(mktRow.packsize);
          if (!Number.isFinite(packSize) || packSize <= 0) continue;
          const annualMarketEU = parseFloat(mktRow.annual_market);
          if (!Number.isFinite(annualMarketEU)) continue;
          annualSUByFacility.set(
            mktRow.facility_cot,
            (annualSUByFacility.get(mktRow.facility_cot) ?? 0) + annualMarketEU / packSize
          );
        }
        const rawSU = (row: OOCProvFacilityRow, mi: number): number => {
          if (!row.isSelected) return 0;
          const pct = row.monthlyPercentages[mi] ?? 0;
          const annualSU = annualSUByFacility.get(row.facilityName);
          if (annualSU !== undefined) return annualSU * (pct / 100);
          const eu = monthlyEU(row, mi);
          return eu / packNum;
        };

        const cmopGroup = rows.filter((r) => !NON_CMOP_FACILITY_COTS.has(r.cot));
        const nonCmopGroup = rows.filter((r) => NON_CMOP_FACILITY_COTS.has(r.cot));
        const showSubtotal = cmopGroup.length > 0 && nonCmopGroup.length > 0;
        // Sum the PER-ROW ROUNDED values (matching renderProvMasGrid in
        // OOCCreateEdit and what's actually displayed per row), not the raw
        // unrounded values — keeps the subtotal/total from drifting 1-2 off
        // what a user gets by adding up the numbers shown on screen.
        const cmopTotals = periods.map((_, mi) =>
          cmopGroup.reduce((s, r) => s + Math.round(monthlyEU(r, mi)), 0)
        );
        const grandTotals = periods.map((_, mi) =>
          rows.reduce((s, r) => s + Math.round(monthlyEU(r, mi)), 0)
        );
        const cmopSuTotals = periods.map((_, mi) =>
          cmopGroup.reduce((s, r) => s + Math.round(rawSU(r, mi)), 0)
        );
        const grandSuTotals = periods.map((_, mi) =>
          rows.reduce((s, r) => s + Math.round(rawSU(r, mi)), 0)
        );

        const renderRow = (row: OOCProvFacilityRow, key: string) => (
          <tr key={key}>
            <td>
              {row.facilityName}
              {row.isContingency && <span className={styles.provContBadge}>CONT</span>}
            </td>
            <td style={{ textAlign: 'right' }}>{row.annualVolSU.toLocaleString()}</td>
            {periods.map((_, mi) => {
              const pct = row.monthlyPercentages[mi] ?? 0;
              const eu = Math.round(monthlyEU(row, mi));
              const su = Math.round(rawSU(row, mi));
              return (
                <td key={mi} style={{ textAlign: 'right', opacity: row.isSelected ? 1 : 0.4 }}>
                  {row.isSelected ? (
                    <>
                      <div className={styles.provPctReadOnly}>{pct}%</div>
                      <div className={styles.provEuVal}>EU: {eu.toLocaleString()}</div>
                      <div className={styles.provSuVal}>SU: {su.toLocaleString()}</div>
                    </>
                  ) : (
                    '-'
                  )}
                </td>
              );
            })}
          </tr>
        );

        return (
          <div key={adj.id} className={styles.provNdcBlock}>
            <ProvNdcHeader adj={adj} />
            <div className={styles.provLegendNote}>
              % = ramp percentage applied to Annual Vol · EU = calculated Each Units (Annual
              Vol × %) · SU = Selling Units (Annual Market ÷ pack size, summed across the
              checked pack sizes for the facility, then scaled by %; Pack {packNum} used where
              no pack-size data is on file)
            </div>
            <div className={styles.tableScrollWrapper}>
              <table className={styles.emailTable}>
                <thead>
                  <tr>
                    <th>Facility</th>
                    <th>Annual Vol (SU)</th>
                    {periods.map((p) => (
                      <th key={p}>
                        {periodToLabel(p)}
                        <div className={styles.provMonthSub}>% / EU / SU</div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {cmopGroup.map((row, idx) => renderRow(row, `cmop-${idx}`))}
                  {showSubtotal && (
                    <tr className={styles.provSubtotalRow}>
                      <td>
                        <strong>CMOP Subtotal</strong>
                      </td>
                      <td></td>
                      {cmopTotals.map((v, mi) => (
                        <td key={mi} style={{ textAlign: 'right' }}>
                          <strong>EU: {v.toLocaleString()}</strong>
                          <br />
                          <strong>SU: {cmopSuTotals[mi].toLocaleString()}</strong>
                        </td>
                      ))}
                    </tr>
                  )}
                  {nonCmopGroup.map((row, idx) => renderRow(row, `noncmop-${idx}`))}
                  <tr className={styles.provTotalRow}>
                    <td>
                      <strong>TOTAL</strong>
                    </td>
                    <td></td>
                    {grandTotals.map((v, mi) => (
                      <td key={mi} style={{ textAlign: 'right' }}>
                        <strong>EU: {v.toLocaleString()}</strong>
                        <br />
                        <strong>SU: {grandSuTotals[mi].toLocaleString()}</strong>
                      </td>
                    ))}
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        );
      })}
    </div>
  );
};

const ProvNcEmailTable: React.FC<{ adjustments: OOCForecastAdjustment[] }> = ({ adjustments }) => {
  if (adjustments.length === 0) return null;
  const periods = generate15MonthPeriods().slice(0, 3);
  const monthLabels = periods.map(periodToLabel);

  return (
    <div>
      <div className={styles.sectionTitleRow}>
        <div className={styles.emailSectionTitle}>Provisional NC</div>
      </div>
      {adjustments.map((adj) => {
        const rows = adj.varianceRows ?? [];

        if (rows.length === 0) {
          return (
            <div key={adj.id} className={styles.provNdcBlock}>
              <ProvNdcHeader adj={adj} />
              <div className={styles.provEmptyNote}>No variance data available for this NDC.</div>
            </div>
          );
        }

        const ncPcts = adj.ncMonthPcts?.length === 3 ? adj.ncMonthPcts : [50, 75, 90];
        const threshold = adj.ncThreshold ?? 15;

        // Matches ooc_email_service._nc_month_fc and the live ProvNC grid's
        // getMonthFC exactly: driven by the operator's Annual NCE (already
        // in SU terms — no pack-size division), NOT historical12MVolume
        // (that's only the variance% comparison baseline).
        const getMonthFC = (nceVolume: number, mi: number): number => {
          const monthly = Math.round(nceVolume / 12);
          return monthly + Math.round(monthly * (ncPcts[mi] / 100));
        };

        const totalHist = rows.reduce((s, r) => s + r.historical12MVolume, 0);
        const totalNCE = rows.reduce((s, r) => s + r.nceVolume, 0);
        const totalVarPct = totalHist > 0 ? ((totalNCE - totalHist) / totalHist) * 100 : 0;
        const isTotalFlagged = Math.abs(totalVarPct) > threshold;
        const monthTotals = [0, 1, 2].map((mi) =>
          rows.reduce((s, r) => s + getMonthFC(r.nceVolume, mi), 0)
        );

        return (
          <div key={adj.id} className={styles.provNdcBlock}>
            <ProvNdcHeader adj={adj} />
            <div className={styles.tableScrollWrapper}>
              <table className={styles.emailTable}>
                <thead>
                  <tr>
                    <th>Agency</th>
                    <th>Historical 12M (SUs)</th>
                    <th>Annual NCE (SUs)</th>
                    <th>Variance</th>
                    {monthLabels.map((lbl, mi) => (
                      <th key={mi}>
                        {lbl}
                        <div className={styles.provMonthSub}>@ {ncPcts[mi]}%</div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => {
                    const varPct =
                      row.historical12MVolume > 0
                        ? ((row.nceVolume - row.historical12MVolume) / row.historical12MVolume) * 100
                        : 0;
                    const isFlagged = Math.abs(varPct) > threshold;
                    return (
                      <tr key={row.agency}>
                        <td>
                          <strong>{row.agency}</strong>
                        </td>
                        <td style={{ textAlign: 'right' }}>{row.historical12MVolume.toLocaleString()}</td>
                        <td style={{ textAlign: 'right' }}>{row.nceVolume.toLocaleString()}</td>
                        <td className={isFlagged ? styles.provFlagged : undefined} style={{ textAlign: 'right' }}>
                          {varPct > 0 ? '+' : ''}
                          {varPct.toFixed(1)}%{isFlagged ? ' ⚠' : ''}
                        </td>
                        {([0, 1, 2] as const).map((mi) => (
                          <td key={mi} style={{ textAlign: 'right' }}>
                            {getMonthFC(row.nceVolume, mi).toLocaleString()}
                          </td>
                        ))}
                      </tr>
                    );
                  })}
                  <tr className={styles.provTotalRow}>
                    <td>
                      <strong>TOTAL</strong>
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      <strong>{totalHist.toLocaleString()}</strong>
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      <strong>{totalNCE.toLocaleString()}</strong>
                    </td>
                    <td className={isTotalFlagged ? styles.provFlagged : undefined} style={{ textAlign: 'right' }}>
                      <strong>
                        {totalVarPct > 0 ? '+' : ''}
                        {totalVarPct.toFixed(1)}%{isTotalFlagged ? ' ⚠' : ''}
                      </strong>
                    </td>
                    {monthTotals.map((v, mi) => (
                      <td key={mi} style={{ textAlign: 'right' }}>
                        <strong>{v.toLocaleString()}</strong>
                      </td>
                    ))}
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        );
      })}
    </div>
  );
};

interface MiniTableProps {
  adjustments: OOCForecastAdjustment[];
  title: string;
  showProbability?: boolean;
}

const MiniEmailTable: React.FC<MiniTableProps> = ({ adjustments, title, showProbability = false }) => {
  if (adjustments.length === 0) return null;
  const monthColumns = adjustments[0]?.forecasts ?? [];

  return (
    <div>
      <div className={styles.sectionTitleRow}>
        <div className={styles.emailSectionTitle}>{title}</div>
        <div className={styles.tableLegend}>
          <span className={styles.increaseArrow}>↑</span> Value adjusted more than 20% above previous
        </div>
      </div>
      <div className={styles.tableScrollWrapper}>
        <table className={styles.emailTable}>
          <thead>
            <tr>
              <th>NDC10</th>
              <th>NDC11</th>
              <th>Supplier</th>
              <th>Concept</th>
              <th>Pack Out</th>
              <th>Reason</th>
              {monthColumns.map((col) => (
                <th key={col.period}>{periodToLabel(col.period)}</th>
              ))}
              {showProbability && <th>Probability</th>}
              <th>Notes</th>
            </tr>
          </thead>
          <tbody>
            {adjustments.map((adj) => {
              const hasPrev = adj.forecasts.some(
                (c) => c.previousValue !== null && c.previousValue !== undefined
              );
              return (
                <React.Fragment key={adj.id}>
                  <tr>
                    <td>{adj.ndc10}</td>
                    <td>{adj.ndc11}</td>
                    <td>{adj.supplier}</td>
                    <td>{adj.concept}</td>
                    <td>{adj.packOut}</td>
                    <td>{adj.reason}</td>
                    {adj.forecasts.map((cell, idx) => {
                      const isUp =
                        cell.previousValue != null &&
                        cell.previousValue > 0 &&
                        cell.value != null &&
                        cell.value > cell.previousValue * 1.2;
                      return (
                        <td key={idx} className={getCellClass(cell.colorType, styles)}>
                          {formatCellValue(cell)}
                          {isUp && <span className={styles.increaseArrow}>↑</span>}
                        </td>
                      );
                    })}
                    {showProbability && <td>{adj.probability ?? '-'}</td>}
                    <td>{adj.notes || '-'}</td>
                  </tr>
                  {hasPrev && (
                    <tr className={styles.prevRow}>
                      <td>Previous</td>
                      <td></td>
                      <td></td>
                      <td></td>
                      <td></td>
                      <td></td>
                      {adj.forecasts.map((cell, idx) => (
                        <td key={idx}>
                          {cell.previousValue != null
                            ? cell.previousValue.toLocaleString('en-US')
                            : ''}
                        </td>
                      ))}
                      {showProbability && <td></td>}
                      <td></td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const OOCCommunicate: React.FC = () => {
  const {
    currentSubmission,
    navigateToDashboard,
    handleExportExcel,
    handleSendEmail,
    handleMarkCommunicated,
    isSendingEmail,
    isExportingExcel,
    emailData,
  } = useOOCCommunicateLogic();

  if (!currentSubmission || !emailData) {
    return <div className={styles.communicateContainer}>No submission selected.</div>;
  }

  const { id, communicatedDate } = currentSubmission;
  const { masAdj, ncAdj, provMasAdj, provNcAdj, toField, subjectField, attachmentField, greetingText, signatureText } = emailData;

  return (
    <div className={styles.communicateContainer}>
      <div className={styles.sectionHeader}>
        <h2>{id} - Communicated to SC</h2>
      </div>

      <div className={styles.successCallout}>
        <CheckCircle2 size={18} className={styles.calloutIcon} />
        <span>
          Communicated to SC on {formatOOCDate(communicatedDate) || 'N/A'}. Email sent to SC, Purchasing, PPS, Business Development, and CSE.
        </span>
      </div>

      <div className={styles.emailPreviewTitle}>Email Preview</div>
      <div className={styles.emailPreviewContainer}>
        <div className={styles.emailHeaderBar}>
          <div className={styles.emailHeaderField}>
            <span className={styles.emailFieldLabel}>To:</span>
            <span className={styles.emailFieldValue}>{toField}</span>
          </div>
          <div className={styles.emailHeaderField}>
            <span className={styles.emailFieldLabel}>Subject:</span>
            <span className={styles.emailFieldValue}>{subjectField}</span>
          </div>
          <div className={styles.emailHeaderField}>
            <span className={styles.emailFieldLabel}>
              <Paperclip size={13} style={{ marginRight: 4, verticalAlign: 'middle' }} />
              Attachment:
            </span>
            <span className={styles.emailFieldValue}>{attachmentField}</span>
          </div>
        </div>

        <div className={styles.emailBody}>
          <div className={styles.emailGreeting}>{greetingText}</div>
          <MiniEmailTable adjustments={masAdj} title="MAS DP" />
          <MiniEmailTable adjustments={ncAdj} title="NC DP" />
          <ProvMasEmailTable adjustments={provMasAdj} />
          <ProvNcEmailTable adjustments={provNcAdj} />
          <div className={styles.emailSignature}>{signatureText}</div>
        </div>
      </div>

      <div className={styles.actionBar}>
        <div className={styles.actionBarLeft}>
          <Button
            label="Back to Dashboard"
            icon={<ArrowLeft size={16} />}
            className={`p-button-text ${styles.backBtn}`}
            onClick={navigateToDashboard}
          />
        </div>
        <div className={styles.actionBarRight}>
          <Button
            label={isExportingExcel ? 'Exporting...' : 'Export Excel'}
            icon={<Download size={16} />}
            className="p-button-outlined"
            onClick={handleExportExcel}
            disabled={isExportingExcel}
            loading={isExportingExcel}
          />
          <Button
            label={isSendingEmail ? 'Sending...' : 'Send Email'}
            icon={<Mail size={16} />}
            className="p-button-outlined"
            onClick={handleSendEmail}
            disabled={isSendingEmail}
            loading={isSendingEmail}
          />
          <Button
            label="Close Submission"
            icon={<CheckCircle2 size={16} />}
            className={styles.markCommunicatedBtn}
            onClick={handleMarkCommunicated}
          />
        </div>
      </div>
    </div>
  );
};

export default observer(OOCCommunicate);
